/**
 * Print helpers for handling document printing
 */

import { logger } from "../utils/logger";

export interface PrintOptions {
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  /**
   * When true and the fetched file is a PDF, skip the hidden iframe path and open
   * the blob URL in a new tab, then trigger print (Chrome’s PDF embed is unreliable).
   */
  openPdfInNewTabForPrint?: boolean;
}

const scheduleBlobCleanup = (blobUrl: string, iframe?: HTMLIFrameElement) => {
  setTimeout(() => {
    try {
      if (iframe?.parentNode) {
        document.body.removeChild(iframe);
      }
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // Ignore cleanup errors
    }
  }, 2000);
};

const isPdfLike = (
  blob: Blob,
  contentTypeHeader: string | null,
  fileName?: string,
): boolean => {
  const ct = `${contentTypeHeader || ""} ${blob.type || ""}`.toLowerCase();
  if (ct.includes("application/pdf")) {
    return true;
  }
  return Boolean(fileName && fileName.toLowerCase().endsWith(".pdf"));
};

const toPdfBlob = (blob: Blob): Blob => {
  if (blob.type === "application/pdf") {
    return blob;
  }
  return new Blob([blob], { type: "application/pdf" });
};

/**
 * Open PDF blob in a new window and print (load + delayed fallback).
 */
const printPdfViaNewTab = (
  blobUrl: string,
  originalUrl: string,
  fileName: string | undefined,
  options?: PrintOptions,
): void => {
  let printed = false;
  const finish = () => {
    if (printed) {
      return;
    }
    printed = true;
    options?.onComplete?.();
    scheduleBlobCleanup(blobUrl);
  };

  const printWindow = window.open(blobUrl, "_blank");
  if (!printWindow) {
    options?.onError?.(
      new Error("Popup blocked or failed to open print window"),
    );
    window.open(originalUrl, "_blank");
    finish();
    return;
  }

  const onWinPrint = () => {
    if (printed) {
      return;
    }
    try {
      printWindow.focus();
      printWindow.print();
    } catch (e) {
      logger.error("New-tab PDF print failed", e);
    }
    finish();
  };

  printWindow.addEventListener("load", onWinPrint, { once: true });
  window.setTimeout(() => {
    if (!printed) {
      onWinPrint();
    }
  }, 500);
};

/**
 * Print a document from a URL
 * Fetches the file, creates a blob URL, and triggers the browser's print dialog
 * This approach works for cross-origin URLs (AWS S3, Firebase Storage, etc.)
 *
 * @param url - The URL of the document to print
 * @param fileName - Optional file name for logging purposes
 * @param options - Optional callbacks for loading state management
 * @returns Promise that resolves when print dialog is opened
 */
export const handlePrintFile = async (
  url: string | undefined,
  fileName?: string,
  options?: PrintOptions,
): Promise<void> => {
  if (!url) {
    logger.error("Cannot print: URL is undefined");
    options?.onError?.(new Error("URL is undefined"));
    return;
  }

  try {
    options?.onStart?.();

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch file for printing");
    }

    const rawBlob = await response.blob();
    const contentType = response.headers.get("content-type");
    const treatAsPdf = isPdfLike(rawBlob, contentType, fileName);
    const blob = treatAsPdf ? toPdfBlob(rawBlob) : rawBlob;
    const blobUrl = window.URL.createObjectURL(blob);

    if (treatAsPdf && options?.openPdfInNewTabForPrint) {
      printPdfViaNewTab(blobUrl, url, fileName, options);
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    let printed = false;
    const finish = () => {
      if (printed) {
        return;
      }
      printed = true;
      options?.onComplete?.();
      scheduleBlobCleanup(blobUrl, iframe);
    };

    const tryPrintFromIframe = () => {
      if (printed) {
        return;
      }
      try {
        const w = iframe.contentWindow;
        if (!w) {
          return;
        }
        w.focus();
        w.print();
        finish();
      } catch (e) {
        logger.error("Iframe print failed", e);
      }
    };

    iframe.onload = () => tryPrintFromIframe();

    if (treatAsPdf) {
      /**
       * Chrome’s PDF embed often skips `load`. Deferred attempts, then new-tab fallback.
       */
      window.setTimeout(() => tryPrintFromIframe(), 380);
      window.setTimeout(() => {
        if (!printed) {
          tryPrintFromIframe();
        }
      }, 900);

      window.setTimeout(() => {
        if (printed) {
          return;
        }
        logger.error(
          "PDF print: iframe did not succeed; falling back to new window",
          fileName ?? "",
        );
        const printWindow = window.open(blobUrl, "_blank");
        if (printWindow) {
          const onWinPrint = () => {
            if (printed) {
              return;
            }
            try {
              printWindow.focus();
              printWindow.print();
            } catch (e) {
              logger.error("Fallback window print failed", e);
            }
            finish();
          };
          printWindow.addEventListener("load", onWinPrint, { once: true });
          window.setTimeout(() => {
            if (!printed) {
              onWinPrint();
            }
          }, 500);
        } else {
          options?.onError?.(
            new Error("Popup blocked or failed to open print window"),
          );
          window.open(url, "_blank");
          finish();
        }
      }, 1400);
    }

    iframe.onerror = () => {
      if (printed) {
        return;
      }
      options?.onError?.(new Error("Failed to load document for printing"));
      finish();
      window.open(url, "_blank");
    };
  } catch (error) {
    logger.error(
      "Error printing file:",
      fileName ?? "(file name unavailable)",
      error,
    );
    options?.onError?.(
      error instanceof Error ? error : new Error(String(error)),
    );
    options?.onComplete?.();
    window.open(url, "_blank");
  }
};

const DOCX_PRINT_DELAY_MS = 800;

/**
 * Fetch a .docx from a URL, render it with docx-preview, and open the browser print dialog.
 * The storage host must allow CORS for fetch (same requirement as {@link handlePrintFile}).
 */
export const printDocFile = async (
  url: string | undefined,
  fileName?: string,
  options?: PrintOptions,
): Promise<void> => {
  if (!url) {
    logger.error("Cannot print DOCX: URL is undefined");
    options?.onError?.(new Error("URL is undefined"));
    return;
  }

  let iframe: HTMLIFrameElement | null = null;

  try {
    options?.onStart?.();

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const { renderAsync } = await import("docx-preview");

    iframe = document.createElement("iframe");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:none;opacity:0;pointer-events:none";
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const idoc = iframe.contentDocument;
    if (!win || !idoc) {
      throw new Error("Iframe document unavailable");
    }

    idoc.open();
    idoc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body { margin: 0; padding: 30px; font-family: sans-serif; }
      </style></head><body></body></html>`,
    );
    idoc.close();

    await renderAsync(arrayBuffer, idoc.body, idoc.head, {
      debug: false,
      experimental: true,
    });

    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch (e) {
        logger.error("DOCX print failed", e);
        options?.onError?.(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (iframe?.parentNode) {
          document.body.removeChild(iframe);
        }
        options?.onComplete?.();
      }
    }, DOCX_PRINT_DELAY_MS);
  } catch (error) {
    logger.error(
      "Error printing DOCX:",
      fileName ?? "(file name unavailable)",
      error,
    );
    if (iframe?.parentNode) {
      document.body.removeChild(iframe);
    }
    options?.onError?.(
      error instanceof Error ? error : new Error(String(error)),
    );
    options?.onComplete?.();
    window.open(url, "_blank");
  }
};
